import React, { useState, useEffect } from 'react';
import { Crown, Users, Trophy, X, Check, AlertCircle } from 'lucide-react';

const MultiWinnerCompletionModal = ({
  tournament,
  selectedWinners,
  onWinnerSelectionChange,
  onConfirm,
  onCancel,
  loading,
  language,
  t,
  getMaxAllowedWinners,
  supportsMultipleWinners
}) => {
  const [localSelectedWinners, setLocalSelectedWinners] = useState(selectedWinners || []);
  const [searchTerm, setSearchTerm] = useState('');

  // UPDATED: Calculate max winners with support for ALL game modes
  const calculateMaxWinners = (tournament) => {
    console.log('🔍 Tournament data for winner calculation:', {
      gameMode: tournament?.gameMode,
      multiplayerType: tournament?.multiplayerType,
      participantCount: (tournament?.participants?.length || 0) + (tournament?.fillers?.length || 0),
      tournament: tournament
    });

    if (!tournament) {
      console.log('⚠️ No tournament data provided');
      return 1;
    }

    const actualParticipants = (tournament.participants?.length || 0) + (tournament.fillers?.length || 0);

    // Battle Royale specific logic (keep existing)
    if (tournament.gameMode === 'battleRoyale') {
      switch (tournament.multiplayerType) {
        case 'duo':
          console.log('✅ Battle Royale Duo - Max winners: 2');
          return 2;
        case 'squad':
          console.log('✅ Battle Royale Squad - Max winners: 4');
          return 4;
        case 'solo':
          console.log('✅ Battle Royale Solo - Max winners: 1');
          return 1;
        default:
          console.log('⚠️ Unknown Battle Royale type:', tournament.multiplayerType, 'defaulting to 1 winner');
          return 1;
      }
    }

    // NEW: All other game modes support flexible winners
    if (actualParticipants <= 2) {
      console.log('ℹ️ Small tournament (≤2 players) - Max winners: 1');
      return 1;
    }
    
    // Allow up to half the participants, with minimum 1 and maximum 5
    const flexibleMax = Math.max(1, Math.min(5, Math.floor(actualParticipants / 2)));
    console.log(`✅ ${tournament.gameMode} tournament with ${actualParticipants} players - Max winners: ${flexibleMax}`);
    
    return flexibleMax;
  };

  // FIXED: Use local calculation first, then fallback to passed function
  const maxWinners = (() => {
    const localMax = calculateMaxWinners(tournament);
    const passedMax = getMaxAllowedWinners ? getMaxAllowedWinners(tournament) : null;
    
    console.log('🎯 Max winners calculation:', {
      localCalculation: localMax,
      passedFunction: passedMax,
      finalValue: passedMax !== null ? passedMax : localMax
    });
    
    return passedMax !== null ? passedMax : localMax;
  })();

  const isMultiWinner = maxWinners > 1;

  console.log('🎯 Modal state:', {
    maxWinners,
    isMultiWinner,
    currentSelectedCount: localSelectedWinners.length,
    gameMode: tournament?.gameMode,
    multiplayerType: tournament?.multiplayerType,
    tournamentObject: tournament
  });

  // Get all participants (real players + fillers)
  const getAllParticipants = () => {
    const realParticipants = (tournament.participants || []).map(p => ({
      _id: p._id,
      name: p.inGameName || p.username,
      type: 'real',
      displayName: p.inGameName || p.username
    }));

    const fillerParticipants = (tournament.fillers || []).map(f => ({
      _id: f._id,
      name: f.name,
      type: 'filler',
      displayName: `${f.name} (Filler)`,
      addedBy: f.addedBy
    }));

    return [...realParticipants, ...fillerParticipants];
  };

  const allParticipants = getAllParticipants();
  
  // Filter participants based on search
  const filteredParticipants = allParticipants.filter(participant =>
    participant.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // FIXED: Handle winner selection with improved logic and better debugging
  const handleWinnerToggle = (participantId) => {
    console.log('🎯 Winner toggle clicked:', {
      participantId,
      currentSelected: localSelectedWinners,
      maxWinners,
      isAlreadySelected: localSelectedWinners.includes(participantId),
      currentCount: localSelectedWinners.length
    });
    
    let newSelectedWinners;
    
    if (localSelectedWinners.includes(participantId)) {
      // Remove winner (always allowed)
      newSelectedWinners = localSelectedWinners.filter(id => id !== participantId);
      console.log('✅ Removing winner. New selection:', newSelectedWinners);
    } else {
      // Add winner
      if (localSelectedWinners.length >= maxWinners) {
        if (maxWinners === 1) {
          // Single winner mode: replace the current winner
          newSelectedWinners = [participantId];
          console.log('✅ Single winner mode: replacing winner. New selection:', newSelectedWinners);
        } else {
          // Multi-winner mode: at max capacity, show feedback but don't add
          console.log('⚠️ Cannot add winner: at max capacity', {
            maxWinners,
            currentCount: localSelectedWinners.length,
            attemptingToAdd: participantId
          });
          // FIXED: Add visual feedback when at capacity
          alert(`Maximum ${maxWinners} winners already selected for ${tournament.gameMode} ${tournament.multiplayerType || ''} mode`);
          return;
        }
      } else {
        // Add winner (under max capacity)
        newSelectedWinners = [...localSelectedWinners, participantId];
        console.log('✅ Adding winner. New selection:', newSelectedWinners);
      }
    }
    
    console.log('🎯 Updating winner selection:', {
      oldSelection: localSelectedWinners,
      newSelection: newSelectedWinners,
      change: newSelectedWinners.length - localSelectedWinners.length
    });
    
    setLocalSelectedWinners(newSelectedWinners);
    onWinnerSelectionChange(newSelectedWinners);
  };

  // UPDATED: Get winner selection display text for all game modes
  const getWinnerSelectionText = () => {
    if (maxWinners === 1) {
      return t('selectWinner', language) || 'Select Winner';
    }
    return t('selectWinners', language, { max: maxWinners }) || `Select Winners (1-${maxWinners})`;
  };

  // UPDATED: Get tournament mode display for all game modes
  const getModeDisplay = () => {
    if (tournament.gameMode === 'battleRoyale') {
      return `Battle Royale ${tournament.multiplayerType}`;
    }
    if (tournament.gameMode === 'quickSolo') {
      return 'Quick Solo (1v1)';
    }
    // Support all other multiplayer modes
    if (tournament.multiplayerType && tournament.multiplayerType !== 'custom') {
      return `${tournament.gameMode} (${tournament.multiplayerType})`;
    }
    return tournament.gameMode;
  };

  // Calculate prize per winner
  const prizePerWinner = localSelectedWinners.length > 0 
    ? Math.floor(tournament.prizePool / localSelectedWinners.length)
    : tournament.prizePool;

  // Validation
  const canConfirm = localSelectedWinners.length > 0 && localSelectedWinners.length <= maxWinners;
  const isOverMaxWinners = localSelectedWinners.length > maxWinners;
  const isUnderMinWinners = localSelectedWinners.length === 0;

  // Update local state when props change
  useEffect(() => {
    setLocalSelectedWinners(selectedWinners || []);
  }, [selectedWinners]);

  // ADDED: Debug effect to log when tournament data changes
  useEffect(() => {
    if (tournament) {
      console.log('🔄 Tournament data updated:', {
        gameMode: tournament.gameMode,
        multiplayerType: tournament.multiplayerType,
        status: tournament.status,
        id: tournament._id,
        fullTournament: tournament
      });
    }
  }, [tournament]);

  if (!tournament) {
    console.log('❌ No tournament data provided to modal');
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              <h3 className="text-xl font-semibold text-gray-900">
                {t('completeTournament', language) || 'Complete Tournament'}
              </h3>
            </div>
            {isMultiWinner && (
              <div className="flex items-center space-x-1 bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                <Crown className="w-4 h-4" />
                <span>{t('multiWinner', language) || 'Multi-Winner'}</span>
              </div>
            )}
          </div>
          <button
            onClick={onCancel}
            disabled={loading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tournament Info */}
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">{t('gameMode', language) || 'Mode'}:</span>
              <div className="font-medium">{getModeDisplay()}</div>
            </div>
            <div>
              <span className="text-gray-500">{t('participants', language) || 'Players'}:</span>
              <div className="font-medium">{allParticipants.length}</div>
            </div>
            <div>
              <span className="text-gray-500">{t('prizePool', language) || 'Prize Pool'}:</span>
              <div className="font-medium">{tournament.prizePool} points</div>
            </div>
            <div>
              <span className="text-gray-500">{t('maxWinners', language) || 'Max Winners'}:</span>
              <div className="font-medium text-blue-600">{maxWinners}</div>
            </div>
          </div>
        </div>

        {/* Winner Selection Info */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h4 className="text-lg font-medium text-gray-900">
              {getWinnerSelectionText()}
              {isMultiWinner && (
                <span className="ml-2 text-sm text-blue-600 font-normal">
                  ({tournament.gameMode === 'battleRoyale' 
                    ? `Battle Royale ${tournament.multiplayerType}`
                    : `${tournament.gameMode} ${tournament.multiplayerType || ''}`
                  })
                </span>
              )}
            </h4>
            <div className="text-right">
              <div className={`text-sm font-medium ${
                localSelectedWinners.length >= maxWinners 
                  ? 'text-green-600' 
                  : localSelectedWinners.length > 0 
                    ? 'text-blue-600' 
                    : 'text-gray-500'
              }`}>
                {t('selected', language) || 'Selected'}: {localSelectedWinners.length}/{maxWinners}
              </div>
              {localSelectedWinners.length > 0 && (
                <div className="text-sm font-medium text-green-600">
                  {prizePerWinner} {t('pointsPerWinner', language) || 'points per winner'}
                  {localSelectedWinners.length > 1 && (
                    <span className="text-xs text-gray-500 block">
                      (Prize split {localSelectedWinners.length} ways)
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* UPDATED: Multi-winner info banner for all game modes */}
          {isMultiWinner && (
            <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex items-center space-x-2 text-blue-800 text-sm">
                <Crown className="w-4 h-4" />
                <span className="font-medium">
                  {tournament.gameMode === 'battleRoyale' && tournament.multiplayerType === 'duo' 
                    ? 'Duo Mode: Select up to 2 winners (teammates share the prize)'
                    : tournament.gameMode === 'battleRoyale' && tournament.multiplayerType === 'squad'
                    ? 'Squad Mode: Select up to 4 winners (squad members share the prize)'
                    : `Multi-winner ${tournament.gameMode}: Select up to ${maxWinners} winners (prize will be split using Math.floor)`
                  }
                </span>
              </div>
              {localSelectedWinners.length === maxWinners && (
                <div className="mt-1 text-xs text-blue-700">
                  Maximum winners selected. Click on selected winners to change your selection.
                </div>
              )}
              <div className="mt-1 text-xs text-blue-600">
                Prize per winner will be calculated using Math.floor (e.g., 145.5 becomes 145)
              </div>
            </div>
          )}

          {/* Debug Info (remove in production) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
              <strong>Debug Info:</strong><br/>
              Game Mode: {tournament.gameMode}<br/>
              Multiplayer Type: {tournament.multiplayerType}<br/>
              Max Winners: {maxWinners}<br/>
              Is Multi Winner: {isMultiWinner ? 'Yes' : 'No'}<br/>
              Selected Count: {localSelectedWinners.length}
            </div>
          )}

          {/* Validation Messages */}
          {isOverMaxWinners && (
            <div className="mt-2 flex items-center space-x-2 text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2">
              <AlertCircle className="w-4 h-4" />
              <span>
                {t('tooManyWinners', language, { max: maxWinners }) || 
                  `Too many winners selected. Maximum allowed: ${maxWinners}`}
              </span>
            </div>
          )}
          
          {isUnderMinWinners && (
            <div className="mt-2 flex items-center space-x-2 text-amber-600 text-sm bg-amber-50 border border-amber-200 rounded p-2">
              <AlertCircle className="w-4 h-4" />
              <span>
                {t('pleaseSelectWinner', language) || 'Please select at least one winner'}
              </span>
            </div>
          )}
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b border-gray-200">
          <input
            type="text"
            placeholder={t('searchParticipants', language) || 'Search participants...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Participant List */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-2">
            {filteredParticipants.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                {searchTerm ? 
                  (t('noParticipantsFound', language) || 'No participants found') :
                  (t('noParticipants', language) || 'No participants')
                }
              </div>
            ) : (
              filteredParticipants.map((participant) => {
                const isSelected = localSelectedWinners.includes(participant._id);
                const canSelect = !isSelected && localSelectedWinners.length < maxWinners;
                
                return (
                  <div
                    key={participant._id}
                    onClick={() => {
                      if (loading) return;
                      console.log('🖱️ Participant clicked:', {
                        participantId: participant._id,
                        participantName: participant.name,
                        isSelected,
                        canSelect,
                        currentSelectionCount: localSelectedWinners.length,
                        maxWinners
                      });
                      handleWinnerToggle(participant._id);
                    }}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border-2 cursor-pointer transition-all
                      ${isSelected 
                        ? 'border-green-500 bg-green-50 text-green-900' 
                        : canSelect
                          ? 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                          : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                      }
                      ${loading ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`
                        w-6 h-6 rounded-full border-2 flex items-center justify-center
                        ${isSelected 
                          ? 'border-green-500 bg-green-500 text-white' 
                          : canSelect
                            ? 'border-gray-300 hover:border-blue-400'
                            : 'border-gray-300 bg-gray-200'
                        }
                      `}>
                        {isSelected && <Check className="w-4 h-4" />}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {participant.type === 'real' ? (
                          <Users className={`w-4 h-4 ${
                            isSelected ? 'text-green-600' :
                            canSelect ? 'text-blue-500' : 'text-gray-400'
                          }`} />
                        ) : (
                          <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
                            isSelected ? 'bg-green-500' :
                            canSelect ? 'bg-gray-400' : 'bg-gray-300'
                          }`}>
                            <span className="text-white text-xs">F</span>
                          </div>
                        )}
                        <div>
                          <span className="font-medium">{participant.displayName}</span>
                          {participant.type === 'filler' && (
                            <span className="text-xs text-gray-500 block">Added as filler</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {isSelected && (
                        <div className="text-sm text-green-600 font-medium flex items-center space-x-1">
                          <Crown className="w-4 h-4" />
                          <span>{t('winner', language) || 'Winner'}</span>
                        </div>
                      )}
                      {!canSelect && !isSelected && (
                        <div className="text-xs text-gray-400">
                          Max reached
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {isMultiWinner && localSelectedWinners.length > 1 ? (
                <span>
                  {t('prizeWillBeSplit', language) || 'Prize will be split between'} {localSelectedWinners.length} {t('winners', language) || 'winners'}
                </span>
              ) : (
                <span>
                  {t('tournamentWillBeCompleted', language) || 'Tournament will be completed'}
                </span>
              )}
            </div>
            
            <div className="flex items-center space-x-3">
              <button
                onClick={onCancel}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {t('cancel', language) || 'Cancel'}
              </button>
              
              <button
                onClick={onConfirm}
                disabled={!canConfirm || loading}
                className={`
                  px-6 py-2 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed
                  ${canConfirm && !loading
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'bg-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {loading ? (
                  <div className="flex items-center space-x-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t('completing', language) || 'Completing...'}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <Trophy className="w-4 h-4" />
                    <span>{t('completeTournament', language) || 'Complete Tournament'}</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MultiWinnerCompletionModal;